import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { Subscription } from "react-apollo";
import AlertModal from "components/AlertModal/AlertModal";
import { useAuthorizedOperator } from "contexts/AuthorizedOperatorContext";
import { withSnackbarConsumer, Color } from "contexts/SnackbarContext/SnackbarContext.jsx";
import { httpClient } from "settings/apolloClient.jsx";
import translateErrorMessage from "utils/translateErrorMessage";
import {
  ApplyingOverlay,
  Button,
  InlineRow,
  Label,
  Meta,
  MiniIcon,
  Note,
  OverlayRing,
  OverlayText,
  OverlayTitle,
  Spinner,
  Title,
  UpdateCard,
} from "./systemUpdateComponents";
import {
  DEVICE_UPDATE_STATE_QUERY,
  REQUEST_DEVICE_UPDATE_MUTATION,
  SUBSCRIBE_TO_DEVICE_UPDATE_STATE,
  UPDATE_CURRENT_USER_QUERY,
} from "./systemUpdateQueries";

const AVAILABLE = "AVAILABLE";
const APPLYING = "APPLYING";
const FAILED = "FAILED";

// An admin does not need to be in command: nobody takes command while the plant is
// idle, which is exactly when it is safest to update. Everyone else — api_user and
// restricted_user included — may apply it while holding command.
const PRIVILEGED_ROLES = ["ADMIN", "SUPERUSER"];

const SIGNAL = {
  amber: "#A86A08",
  blue: "#0E77D2",
  red: "#A32A1F",
  grey: "#8798AD",
};

const canApplyUpdate = (currentUser, operator) => {
  if (!currentUser) return false;
  if (PRIVILEGED_ROLES.indexOf(currentUser.role) !== -1) return true;

  return !!operator && operator.id === currentUser.id;
};

const shortCommit = commit => (commit ? commit.substring(0, 7) : null);

// Absinthe subscriptions can go silently dead after a socket reconnect, leaving the
// card frozen until a page reload, so the query polls as a safety net.
const POLL_INTERVAL = 10000;

// Poll answers only replace the state when the controller has reported since — an
// in-flight response must not undo an event that already arrived over the socket.
const takePolled = (previous, polled) => {
  if (!previous) return polled;

  const before = Date.parse(previous.lastHeartbeatAt) || 0;
  const after = Date.parse(polled.lastHeartbeatAt) || 0;

  return after > before ? polled : previous;
};

// Render prop instead of useSubscription: the app's ApolloProvider comes from
// react-apollo v2, which does not populate the context @apollo/react-hooks reads, so
// its hooks only work with an explicit client (see every useQuery in this codebase).
// An explicit httpClient is no help here either — it has no websocket link — and this
// is how the panel's other subscriptions are wired. Renders nothing.
const DeviceUpdateStateSubscriber = ({ onData }) => (
  <Subscription
    subscription={SUBSCRIBE_TO_DEVICE_UPDATE_STATE}
    fetchPolicy="no-cache"
    onSubscriptionData={({ subscriptionData }) => {
      const updated = subscriptionData
        && subscriptionData.data
        && subscriptionData.data.subscribeToDeviceUpdateState;

      if (updated) onData(updated);
    }}
  >
    {() => null}
  </Subscription>
);

const SystemUpdate = ({ snackbarContext }) => {
  const [state, setState] = useState(null);
  const { operator } = useAuthorizedOperator();

  const { data: currentUserData } = useQuery(UPDATE_CURRENT_USER_QUERY, {
    client: httpClient,
  });

  const { data: queryData } = useQuery(DEVICE_UPDATE_STATE_QUERY, {
    fetchPolicy: "network-only",
    pollInterval: POLL_INTERVAL,
    client: httpClient,
  });

  useEffect(() => {
    if (queryData && queryData.deviceUpdateState) {
      setState(previous => takePolled(previous, queryData.deviceUpdateState));
    }
  }, [queryData]);

  const status = state && state.status;

  useEffect(() => {
    if (status === AVAILABLE) {
      snackbarContext.setNotificationTimeOut(
        Color.info,
        "Uma atualização do sistema está disponível para instalar."
      );
    }
    // Fires on the transition into AVAILABLE only. snackbarContext is deliberately
    // left out: it is a fresh object on every provider render, and tracking it would
    // announce the same update again on unrelated re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const [requestUpdate, { loading: requesting }] = useMutation(REQUEST_DEVICE_UPDATE_MUTATION, {
    client: httpClient,
    onCompleted: data => {
      if (data && data.requestDeviceUpdate) setState(data.requestDeviceUpdate);
    },
    onError: error => {
      snackbarContext.setNotificationTimeOut(Color.danger, translateErrorMessage(error));
    },
  });

  const currentUser = currentUserData && currentUserData.getCurrentUser;
  const allowed = canApplyUpdate(currentUser, operator);

  const confirmAndApply = () => {
    AlertModal.fire({
      title: "Atualizar o sistema agora?",
      type: "warning",
      html: (
        <div style={{ textAlign: "left", fontSize: "0.9rem" }}>
          <p>A usina fica indisponível por cerca de um minuto:</p>
          <ul>
            <li>a tela recarrega sozinha ao voltar</li>
            <li>a automação para e volta no mesmo estado</li>
            <li>nenhuma carga pode estar em andamento</li>
          </ul>
        </div>
      ),
      showCancelButton: true,
      confirmButtonColor: "#1D84C6",
      confirmButtonText: "Atualizar",
      cancelButtonText: "Agora não",
      reverseButtons: true,
    }).then(result => {
      if (result.value) requestUpdate();
    });
  };

  const blocked = state && state.blockedReason;
  // The controller polls for the request every few seconds, so the state stays
  // AVAILABLE for a moment after the click. Without this the button would look
  // clickable again in that gap and invite a second request.
  const waitingForController = !!(state && state.requestedAt);

  const buttonLabel = () => {
    if (waitingForController) return "Iniciando a atualização...";
    if (blocked) return "Aguardando fim da carga";

    return "Atualizar sistema";
  };

  // The card exists only when there is something to decide. While the release is
  // still downloading — or while the controller is not reporting at all — the
  // operator has no decision to make, and the automation status widget already
  // covers a controller that went quiet.
  const renderCard = () => {
    if (status === APPLYING) {
      return (
        <React.Fragment>
          <UpdateCard signal={SIGNAL.blue}>
            <MiniIcon className="update-mini-icon nc-icon nc-cloud-download-93" signal={SIGNAL.blue} />
            <Label signal={SIGNAL.blue}>Atualizando</Label>
            <InlineRow>
              <Spinner signal={SIGNAL.blue} />
              <Title>Reiniciando os serviços</Title>
            </InlineRow>
          </UpdateCard>

          {/* Portal: inside the sidebar its own z-index only competes within the
              sidebar's stacking context, leaving the sensors and control menu on top. */}
          {ReactDOM.createPortal(
            <ApplyingOverlay>
              <OverlayRing />
              <OverlayTitle>Atualizando o sistema</OverlayTitle>
              <OverlayText>
                Não desligue o equipamento. A tela volta sozinha em alguns instantes.
              </OverlayText>
            </ApplyingOverlay>,
            document.body
          )}
        </React.Fragment>
      );
    }

    if (status === FAILED) {
      return (
        <UpdateCard signal={SIGNAL.red}>
          <MiniIcon className="update-mini-icon nc-icon nc-cloud-download-93" signal={SIGNAL.red} />
          <Label signal={SIGNAL.red}>Falha na atualização</Label>
          <Title>Não foi possível instalar a atualização</Title>
          <Note>Se repetir, contate o suporte.</Note>
          {allowed && (
            <Button signal={SIGNAL.red} onClick={confirmAndApply} disabled={requesting}>
              Tentar novamente
            </Button>
          )}
        </UpdateCard>
      );
    }

    if (status !== AVAILABLE) return null;

    const signal = blocked || !allowed ? SIGNAL.grey : SIGNAL.amber;

    return (
      <UpdateCard signal={signal}>
        <MiniIcon className="update-mini-icon nc-icon nc-cloud-download-93" signal={signal} />
        <Label signal={signal}>Atualização disponível</Label>
        <Title>Uma nova versão está pronta para instalar</Title>

        {state.currentCommit && (
          <Meta>
            atual
            {" "}
            {shortCommit(state.currentCommit)}
          </Meta>
        )}

        {blocked && <Note>{blocked}</Note>}
        {!blocked && !allowed && (
          <Note>Só o operador em comando ou um administrador pode atualizar.</Note>
        )}

        <Button
          signal={signal}
          onClick={confirmAndApply}
          disabled={!!blocked || !allowed || requesting || waitingForController}
        >
          {buttonLabel()}
        </Button>
      </UpdateCard>
    );
  };

  // The subscriber is mounted regardless of what the card shows: it is what tells the
  // card to appear, so unmounting it while hidden would leave the operator blind to a
  // release that just finished downloading.
  return (
    <React.Fragment>
      <DeviceUpdateStateSubscriber onData={setState} />
      {renderCard()}
    </React.Fragment>
  );
};

export default withSnackbarConsumer(SystemUpdate);
